// =============================================================================
// kb-client.ts — Client API de la KNOWLEDGE BASE (R15-COM-04), DOUBLABLE.
// =============================================================================
// COM-04 = archivage CONSULTABLE des fils de chat clôturés (DBU §2.7.4 + §3.9). DEUX PROFILS, DEUX
// PRÉFIXES, un contrat de LECTURE partagé + un contrat ADMIN étendu (tags + export) :
//   - ADMIN (KB Admin, transverse) → base `/api/communication/admin/kb` (AdminSessionGuard ; mutations
//     exigent l'en-tête CSRF double-submit `x-csrf-token` = cookie non-httpOnly `admin_csrf`) ;
//   - FOURNISSEUR (« Mes conversations archivées », §3.9) → base `/api/communication/kb`
//     (TenantInterceptor) : SOUS-ENSEMBLE STRICT en LECTURE SEULE — AUCUN tag, AUCUN export.
//
// PII (critique) : les messages archivés sont du CONTENU NOMINATIF, restitué UNIQUEMENT via ces réponses
// REST re-cloisonnées par le RLS à l'appelant autorisé. JAMAIS sur le bus, JAMAIS sur le socket, JAMAIS
// vers Pulse. Le CONTENU (messages, snippet) est du TEXTE ARBITRAIRE : rendu JSX ÉCHAPPÉ côté écran,
// JAMAIS `dangerouslySetInnerHTML`. Le siret/contactId viennent de la SESSION côté API (jamais du body).
// ZÉRO donnée métier en localStorage (RULE_REF_006). Horodatages = ISO 8601 (string).

/** Profil d'appel : gouverne le préfixe de base ET (pour les mutations admin) le cookie CSRF. */
export type KbProfile = 'admin' | 'supplier';

/** État d'un fil (parité base). La KB ne consulte QUE les fils `Archive`. */
export type KbThreadStateValue = 'Provisionne' | 'Actif' | 'Archive';

/** Motif d'archivage (parité base — API `kb.dto.ts`). */
export type KbThreadArchiveReasonValue = 'CycleClosed' | 'NegoClosed' | 'Abandoned';

/** Type d'émetteur d'un message (parité base). */
export type KbSenderType = 'Admin' | 'SupplierContact';

/** Rôle de l'émetteur capturé à l'envoi (parité base). */
export type KbSenderRole = 'Admin' | 'Master' | 'Collaborateur';

// Sentinelles de mise en exergue (ts_headline). Unicode Private Use Area : jamais dans du contenu
// légitime, donc split déterministe côté front (rendu <mark> via éléments React ÉCHAPPÉS — jamais
// d'HTML brut). Parité EXACTE avec l'API (`KB_HIGHLIGHT_START`/`KB_HIGHLIGHT_END`).
export const KB_HIGHLIGHT_START = '\uE000';
export const KB_HIGHLIGHT_END = '\uE001';

/** Un tag assigné à un fil (vue liste/détail admin). `[]` côté fournisseur. */
export interface KbThreadTagDto {
  readonly tagId: string;
  readonly label: string;
}

/** Une ligne de la liste des fils archivés (consultation KB). Parité DTO API. */
export interface KbThreadSummaryDto {
  readonly threadId: string;
  readonly campaignId: string;
  /** SIRET du fournisseur du fil (admin ; le fournisseur ne voit que le sien). */
  readonly supplierSiret: string;
  readonly state: KbThreadStateValue;
  /** ISO 8601 — date d'archivage, ou `null`. */
  readonly archivedAt: string | null;
  readonly archivedReason: KbThreadArchiveReasonValue | null;
  /** Nombre de messages du fil. */
  readonly messageCount: number;
  /** ISO 8601 — 1er message (début de période d'activité), ou `null`. */
  readonly firstMessageAt: string | null;
  /** ISO 8601 — dernier message (fin de période d'activité), ou `null`. */
  readonly lastMessageAt: string | null;
  /**
   * Extrait mis en exergue (ts_headline) issu du meilleur message correspondant, ou `null` si aucune
   * recherche (`q` absent). Correspondances encadrées par KB_HIGHLIGHT_START / KB_HIGHLIGHT_END
   * (TEXTE, jamais d'HTML) — PII, REST-only. Parsé/rendu côté écran (`parseKbSnippet` + `<mark>`).
   */
  readonly snippet: string | null;
  /** Tags assignés (ADMIN uniquement ; toujours `[]` côté fournisseur). */
  readonly tags: readonly KbThreadTagDto[];
}

/** Réponse de la liste des fils archivés (consultation KB). */
export interface KbThreadsListDto {
  readonly threads: readonly KbThreadSummaryDto[];
  /** Nombre total de fils du périmètre (après filtres). */
  readonly total: number;
}

/** Un message d'un fil archivé (vue détail, LECTURE SEULE). `content` = texte brut, PII, REST-only. */
export interface KbMessageDto {
  readonly messageId: string;
  readonly senderType: KbSenderType;
  /** Ref opaque de l'auteur (contactId ou subject admin). */
  readonly senderRef: string;
  readonly senderRoleAtSend: KbSenderRole;
  /** Contenu texte brut (PII, REST-only) — rendu JSX ÉCHAPPÉ. */
  readonly content: string;
  /** ISO 8601. */
  readonly sentAt: string;
}

/** Un participant au fil (agrégé depuis les messages). */
export interface KbParticipantDto {
  readonly senderRef: string;
  readonly senderType: KbSenderType;
  /** Nombre de messages émis par ce participant. */
  readonly messageCount: number;
}

/** Réponse de la vue détail d'un fil archivé (§2.7.4 : LECTURE SEULE, méta-données). */
export interface KbThreadDetailDto {
  readonly threadId: string;
  readonly campaignId: string;
  readonly supplierSiret: string;
  readonly state: KbThreadStateValue;
  readonly archivedAt: string | null;
  readonly archivedReason: KbThreadArchiveReasonValue | null;
  readonly messageCount: number;
  /** Période d'activité (min/max sent_at), ISO 8601 ou `null`. */
  readonly periodFrom: string | null;
  readonly periodTo: string | null;
  /** Contacts ayant participé (§2.7.4). */
  readonly participants: readonly KbParticipantDto[];
  readonly messages: readonly KbMessageDto[];
  /** Tags assignés (ADMIN uniquement ; toujours `[]` côté fournisseur). */
  readonly tags: readonly KbThreadTagDto[];
}

/** Un tag de la taxonomie KB (admin-scope). */
export interface KbTagDto {
  readonly tagId: string;
  readonly label: string;
  /** Parent taxonomique (hiérarchie), ou `null` si tag racine. */
  readonly parentTagId: string | null;
  /** ISO 8601. */
  readonly createdAt: string;
}

/** Réponse de la liste de la taxonomie (admin-scope). */
export interface KbTagsListDto {
  readonly tags: readonly KbTagDto[];
}

/** Résultat de création d'un tag. */
export interface CreateTagResultDto {
  readonly tagId: string;
}

/** Format d'export structuré (§2.7.4 / §5.5.3 : JSON ou CSV) — ADMIN ONLY. */
export type KbExportFormat = 'json' | 'csv';

/**
 * Filtres de recherche/liste (état de VUE porté par l'URL, RULE_REF_006). `q` = recherche full-text.
 * `supplierSiret` n'a de sens QUE pour l'admin (le fournisseur ne voit que le sien) : ignoré côté supplier.
 * `archivedFrom`/`archivedTo` = bornes de période (ISO date). Clés absentes = pas de filtre (URL propre).
 */
export interface KbFilters {
  readonly q?: string;
  readonly campaignId?: string;
  /** ADMIN uniquement : filtre par SIRET fournisseur. */
  readonly supplierSiret?: string;
  readonly archivedFrom?: string;
  readonly archivedTo?: string;
}

/** Genre d'erreur applicative de la KB, mappé pour un message UX explicite (parité chat). */
export type KbApiErrorKind = 'unauthorized' | 'forbidden' | 'not-found' | 'generic';

/**
 * Erreur applicative renvoyée par l'API KB (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle ne
 * réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet un libellé UX ciblé.
 */
export class KbApiError extends Error {
  public readonly kind: KbApiErrorKind;

  public constructor(message: string, kind: KbApiErrorKind = 'generic') {
    super(message);
    this.name = 'KbApiError';
    this.kind = kind;
  }
}

/**
 * Contrat de LECTURE de la KB — surface COMMUNE aux deux profils (admin transverse, fournisseur restreint
 * à SES fils par le RLS). Aucune écriture, aucun tag, aucun export : c'est le sous-ensemble §3.9.
 */
export interface KbClient {
  /** GET /threads — liste/recherche des fils archivés (FTS `q`, campagne, période ; +ADMIN : SIRET). */
  listThreads(filters: KbFilters): Promise<KbThreadsListDto>;
  /** GET /threads/:threadId — vue détail LECTURE SEULE (méta + participants + messages + tags). */
  getThreadDetail(threadId: string): Promise<KbThreadDetailDto>;
}

/**
 * Contrat ADMIN de la KB — étend la LECTURE avec la taxonomie (lister/créer/supprimer), l'assignation
 * fil ↔ tag, et la construction des URL d'export (téléchargement navigateur, Content-Disposition serveur).
 * Ces capacités sont ADMIN-ONLY (§3.9 : aucun tag / aucun export côté fournisseur).
 */
export interface KbAdminClient extends KbClient {
  /** GET /tags — taxonomie complète (admin-scope). */
  listTags(): Promise<KbTagsListDto>;
  /** POST /tags — crée un tag (`label`, `parentTagId?`). */
  createTag(label: string, parentTagId?: string): Promise<CreateTagResultDto>;
  /** DELETE /tags/:tagId — supprime un tag de la taxonomie. */
  deleteTag(tagId: string): Promise<void>;
  /** POST /threads/:threadId/tags/:tagId — assigne un tag à un fil (idempotent). */
  assignTag(threadId: string, tagId: string): Promise<void>;
  /** DELETE /threads/:threadId/tags/:tagId — retire l'assignation d'un tag (idempotent). */
  unassignTag(threadId: string, tagId: string): Promise<void>;
  /**
   * Construit l'URL absolue d'export (JSON/CSV) du périmètre filtré, pour un `<a href>` de téléchargement.
   * Le serveur pose l'en-tête Content-Disposition (attachment) ; pas de fetch+blob nécessaire.
   */
  buildExportUrl(format: KbExportFormat, filters: KbFilters): string;
}
